import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories } from '@/mocks/categories';
import { videos } from '@/mocks/videos';
import { siteConfig } from '@/mocks/siteConfig';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTag, setActiveTag] = useState('全部');
  const [sortBy, setSortBy] = useState('hot');

  useEffect(() => {
    setActiveTag('全部');
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];

  const categoryVideos = videos.filter((v) => v.category === currentCategory.id);

  const allTags = ['全部', ...Array.from(new Set(categoryVideos.flatMap((v) => v.tags)))];

  const filteredVideos = categoryVideos
    .filter((v) => activeTag === '全部' || v.tags.includes(activeTag))
    .sort((a, b) => {
      if (sortBy === 'hot') return b.views - a.views;
      if (sortBy === 'new') return b.year.localeCompare(a.year);
      if (sortBy === 'rating') return b.rating - a.rating;
      return 0;
    });

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - ${siteConfig.name}`,
    description: currentCategory.description,
    url: `${siteConfig.url}/category/${currentCategory.slug}`,
    isPartOf: { '@id': `${siteConfig.url}/#website` },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: siteConfig.url },
        { '@type': 'ListItem', position: 2, name: currentCategory.name },
      ],
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: currentCategory.name },
          ]}
        />

        {/* Category Header */}
        <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-50">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center">
                <i className={`${currentCategory.icon} text-primary-600 text-2xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  共 {currentCategory.count}+ 部资源
                </p>
              </div>
            </div>
            <p className="text-sm text-foreground-600 max-w-2xl leading-relaxed">
              {currentCategory.description}
            </p>
          </div>
        </section>

        {/* Filters */}
        <section className="w-full px-4 md:px-6 py-4 bg-background-100 border-b border-background-200/70 sticky top-16 z-30">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-2 flex-wrap">
                {allTags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => setActiveTag(tag)}
                    className={`px-4 py-1.5 text-xs font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                      activeTag === tag
                        ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                        : 'bg-background-50 text-foreground-600 border border-background-200/70 hover:border-primary-200 hover:text-primary-500'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-400 whitespace-nowrap">排序：</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-1.5 text-xs bg-background-50 border border-background-200/70 rounded-full text-foreground-700 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  <option value="hot">最热门</option>
                  <option value="new">最新</option>
                  <option value="rating">评分最高</option>
                </select>
              </div>
            </div>
          </div>
        </section>

        {/* Video Grid */}
        <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-50">
          <div className="max-w-[1400px] mx-auto">
            {filteredVideos.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                {filteredVideos.map((video) => (
                  <VideoCard key={video.id} {...video} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-video-off-line text-foreground-300 text-2xl"></i>
                </div>
                <p className="text-sm text-foreground-500">暂无匹配的视频资源</p>
                <button
                  onClick={() => setActiveTag('全部')}
                  className="mt-3 px-4 py-2 text-xs font-medium text-primary-500 bg-primary-50 rounded-full hover:bg-primary-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  清除筛选
                </button>
              </div>
            )}
          </div>
        </section>

        {/* All Categories Quick Links */}
        <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-100">
          <div className="max-w-[1400px] mx-auto">
            <h3 className="text-base font-heading font-semibold text-foreground-950 mb-4">
              浏览其他分类
            </h3>
            <div className="flex items-center gap-3 flex-wrap">
              {categories
                .filter((c) => c.slug !== slug)
                .map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-50 border border-background-200/70 text-sm text-foreground-600 hover:border-primary-200 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${cat.icon} text-xs`}></i>
                    </div>
                    {cat.name}
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}